import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";

import { initializeAnalytics } from "../utils/analytics";
import {
  acceptAllCookies,
  acceptEssentialOnly,
  ConsentPreferences,
  hasConsentDecision,
  saveConsentPreferences,
  trackConsentEvent,
} from "../utils/consent";

interface CookieConsentProps {
  /**
   * Callback fired when user makes a consent decision
   */
  onConsentChange?: (accepted: boolean) => void;
}

/**
 * Cookie Consent Banner Component
 *
 * GDPR/CCPA compliant cookie consent banner that:
 * - Shows on first visit (no stored consent)
 * - Provides "Accept All" and "Preferences" options
 * - Stores consent in localStorage
 * - Triggers analytics initialization after consent
 *
 * @see https://koru.guide/legal for Privacy Policy
 */
export function CookieConsent({ onConsentChange }: CookieConsentProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<
    Omit<ConsentPreferences, "timestamp">
  >({
    analytics: false,
    marketing: false,
  });

  // Check consent on mount - only show if no decision made
  useEffect(() => {
    if (!hasConsentDecision()) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    acceptAllCookies();
    setIsVisible(false);
    onConsentChange?.(true);

    // Initialize analytics after consent
    initializeAnalytics();

    // Track consent event for compliance records
    trackConsentEvent("accepted");
  };

  const handleRejectAll = () => {
    acceptEssentialOnly();
    setIsVisible(false);
    onConsentChange?.(false);

    // Track rejection for compliance records
    trackConsentEvent("rejected");
  };

  const handleSavePreferences = () => {
    saveConsentPreferences(preferences);
    setIsVisible(false);
    onConsentChange?.(preferences.analytics);

    // Initialize analytics if consent given
    if (preferences.analytics) {
      initializeAnalytics();
    }

    // Track customized consent for compliance records
    trackConsentEvent("customized", preferences);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-consent-title"
          data-testid="cookie-consent-banner"
        >
          <div className="mx-auto max-w-4xl rounded-xl border border-border bg-background/95 backdrop-blur-lg shadow-2xl">
            <AnimatePresence mode="wait">
              {!showPreferences ? (
                <motion.div
                  key="main"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="p-4 md:p-6"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex-1">
                      <h2
                        id="cookie-consent-title"
                        className="text-lg font-semibold text-foreground"
                      >
                        🍪 We value your privacy
                      </h2>
                      <p className="mt-1 text-sm text-muted-foreground">
                        We use cookies to enhance your experience and analyze
                        our traffic. By clicking &quot;Accept All&quot;, you
                        consent to our use of cookies. Read our{" "}
                        <a
                          href="/legal#privacy"
                          className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
                        >
                          Privacy Policy
                        </a>{" "}
                        for more information.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => setShowPreferences(true)}
                        className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors hover:bg-muted/50"
                        data-testid="cookie-preferences-btn"
                      >
                        Preferences
                      </button>
                      <button
                        onClick={handleRejectAll}
                        className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors hover:bg-muted/50"
                        data-testid="cookie-reject-btn"
                      >
                        Reject All
                      </button>
                      <button
                        onClick={handleAcceptAll}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors shadow-sm"
                        data-testid="cookie-accept-btn"
                      >
                        Accept All
                      </button>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="preferences"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="p-4 md:p-6"
                >
                  <h2 className="text-lg font-semibold text-foreground mb-4">
                    Cookie Preferences
                  </h2>
                  <div className="space-y-4">
                    {/* Essential Cookies - Always enabled */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div>
                        <h3 className="font-medium text-foreground">
                          Essential Cookies
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Required for the website to function. Cannot be
                          disabled.
                        </p>
                      </div>
                      <div className="w-12 h-6 bg-primary/50 rounded-full flex items-center justify-end px-1 cursor-not-allowed opacity-70">
                        <div className="w-4 h-4 bg-primary rounded-full" />
                      </div>
                    </div>

                    {/* Analytics Cookies */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div>
                        <h3 className="font-medium text-foreground">
                          Analytics Cookies
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Help us understand how visitors use our site.
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          setPreferences(prev => ({
                            ...prev,
                            analytics: !prev.analytics,
                          }))
                        }
                        className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                          preferences.analytics
                            ? "bg-primary justify-end"
                            : "bg-muted justify-start"
                        }`}
                        role="switch"
                        aria-checked={preferences.analytics}
                        data-testid="analytics-toggle"
                      >
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm" />
                      </button>
                    </div>

                    {/* Marketing Cookies */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div>
                        <h3 className="font-medium text-foreground">
                          Marketing Cookies
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Used to deliver relevant ads and track campaigns.
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          setPreferences(prev => ({
                            ...prev,
                            marketing: !prev.marketing,
                          }))
                        }
                        className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                          preferences.marketing
                            ? "bg-primary justify-end"
                            : "bg-muted justify-start"
                        }`}
                        role="switch"
                        aria-checked={preferences.marketing}
                        data-testid="marketing-toggle"
                      >
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-6">
                    <button
                      onClick={() => setShowPreferences(false)}
                      className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors"
                    >
                      ← Back
                    </button>
                    <button
                      onClick={handleSavePreferences}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors shadow-sm"
                      data-testid="save-preferences-btn"
                    >
                      Save Preferences
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
